import {
    AppointmentListingModel,
    HospitalModel,
    HospitalScheduleModel,
    HospitalScheduleTemplateModel,
    IAppointmentListingModel,
    IHospitalModel,
    IHospitalScheduleModel,
    IHospitalScheduleTemplateModel,
    IVeterinarianModel,
    VeterinarianModel,
    schedule,
    InvoiceModel,
    IInvoiceModel,
    IMacroModel,
    MacroModel,
    IIvlsDeviceModel,
    IvlsDeviceModel
} from "@common/models";

import { HospitalScheduleDateRangeType } from "@common/models/Enums/HospitalScheduleDateRangeType";
import { HospitalScheduleRequestModel, HospitalScheduleTemplateRequest, IAppointmentCheckInRequest, EntityScheduleOverridePreferenceRequest, IVisitCheckoutRequest } from "@common/models/requests";
import { IAppointmentStatusRequest } from "@common/models/requests/AppointmentStatusRequest";
import { IAppointmentRequest } from "@common/models/requests/AppointmentRequest";
import { http } from "@common/utils";
import { DateTime } from "luxon";
import { trackPromise } from "react-promise-tracker";
import { ServiceHelper } from "./serviceHelper";
import { ServiceMethodOptions } from "./types";
import { HospitalPrescriptionRefillModel, IHospitalPrescriptionRefillModel } from "@common/models/HospitalPrescriptionRefillModel";
import { IAddHospitalPrescriptionRefillRequest } from "@common/models/requests/AddHospitalPrescriptionRefillRequest";
import { IUpdateHospitalPrescriptionRefillRequest } from "@common/models/requests/UpdateHospitalPrescriptionRefillRequest";
import { ICheckOutHospitalPrescriptionRefillRequest } from "@common/models/requests/CheckOutHospitalPrescriptionRefillRequest";
import { MacroRequest } from "@common/models/requests/MacroRequest";
import { HospitalPrescriptionModel, IHospitalPrescriptionModel } from "@common/models/HospitalPrescriptionModel";
import { PetNotPresentVisitRequest } from "@common/models/requests/PetNotPresentVisitRequest";
import { CheckoutPetNotPresentVisitRequest } from "@common/models/requests/CheckoutPetNotPresentVisitRequest";
import { HospitalPrescriptionCancelModel } from "@common/models/HospitalPrescriptionCancelModel";

export class HospitalService {
    private static baseUrl: string = '/api/hospital/';

    public static tags = {
        loadScheduledVets: "loadScheduledVets",
        getAppointment: "getAppointment",
        getAppointments: "getAppointments",
        getLastModifiedAppointmentDate: "getLastModifiedAppointmentDate",
        createAppointment: "createAppointment",
        updateAppointment: "updateAppointment",
        checkInAppointment: "checkInAppointment",
        setNoShowAppointment: "setNoShowAppointment",
        setInProgressAppointment: "setInProgressAppointment",
        getHospital: "getHospital",
        convertStoreToHospital: "convertStoreToHospital",
        getAllHospitals: "getAllHospitals",
        updateHospital: "updateHospital",
        getHospitalVeterinarians: "getHospitalVeterinarians",
        getHospitalSchedules: "getHospitalSchedules",
        updateHospitalSchedules: "updateHospitalSchedules",
        setAdmittedAppointment: "setAdmittedAppointment",
        setBookedAppointment: "setBookedAppointment",
        setConfirmedAppointment: "setConfirmedAppointment",
        setCancelledAppointment: "setCancelledAppointment",
        setFinalizedAppointment: "setFinalizedAppointment",
        setPaidAppointment: "setPaidAppointment",
        addAdhocHospitalSchedule: "addAdhocHospitalSchedule",
        getHospitalScheduleTemplate: "getHospitalScheduleTemplate",
        getHospitalScheduleTemplateList: "getHospitalScheduleTemplateList",
        createHospitalSchedulePreferenceOverrides: "createHospitalSchedulePreferenceOverrides",
        updateHospitalSchedulePreferenceOverrides: "updateHospitalSchedulePreferenceOverrides",
        reopenVisit: "reopenVisit",
        createHospitalPrescriptionRefill: "createHospitalPrescriptionRefill",
        updateHospitalPrescriptionRefill: "updateHospitalPrescriptionRefill",
        checkOutHospitalPrescriptionRefill: "checkOutHospitalPrescriptionRefill",
        getMacro: "getMacro",
        updateMacro: "updateMacro",
        createMacro: "createMacro",
        deleteMacro: "deleteMacro",
        upsertHospitalPrescription: "upsertHospitalPrescription",
        upsertHospitalPrescriptions: "upsertHospitalPrescriptions",
        deleteHospitalPrescription: "deleteHospitalPrescription",
        refreshHospitalIvlsDevices: "refreshHospitalIvlsDevices",
        setAbortedAppointment: "setAbortedAppointment",
        createPetNotPresentVisit: "createPetNotPresentVisit",
        checkOutPetNotPresentVisit: "checkOutPetNotPresentVisit",
        cancelPetNotPresentVisit: "cancelPetNotPresentVisit",
        cancelPetNotPresentInvoice: "cancelPetNotPresentInvoice",
        cancelHospitalPrescription: "cancelHospitalPrescription"
    }

    // #region Get Appointment/s
    public static getAppointment = (hospitalId: number, reservationId: number, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(new Promise<AppointmentListingModel>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/appointments/${reservationId}`)
                .get<IAppointmentListingModel>()
                .then((appointment) => {
                    try {
                        const appointmentClass = new AppointmentListingModel(appointment);
                        resolve(appointmentClass)
                    }
                    catch (err) {
                        reject(err)
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        }), ServiceHelper.getTagForOptions(this.tags.getAppointment, options));
    }

    public static getAppointments = (hospitalId: number, startDateTime: DateTime, endDateTime: DateTime, options?: ServiceMethodOptions): Promise<AppointmentListingModel[]> => {
        return trackPromise(new Promise<AppointmentListingModel[]>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/appointments?startDate=${startDateTime.toISO({ includeOffset: false })}&endDate=${endDateTime.toISO({ includeOffset: false })}`)
                .get<IAppointmentListingModel[]>()
                .then((appointments) => {
                    const appointmentClasses = appointments.map((apt) => new AppointmentListingModel(apt));
                    ServiceHelper.showSuccess("Appointments Loaded!", options);
                    resolve(appointmentClasses)
                })
                .catch((error) => {
                    ServiceHelper.showError(`Appointments failed to load! ${error.message}`, options);
                    reject(error);
                });
        }), ServiceHelper.getTagForOptions(this.tags.getAppointments, options));
    }

    public static getLastModifiedAppointmentDate = (hospitalId: number, startDateTime: DateTime, endDateTime: DateTime, options?: ServiceMethodOptions): Promise<string> => {
        return trackPromise(new Promise<string>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/appointments/lastmodifieddate?startDate=${startDateTime.toISO({ includeOffset: false })}&endDate=${endDateTime.toISO({ includeOffset: false })}`)
                .get<string>()
                .then((lastModifiedDate) => {
                    resolve(lastModifiedDate);
                })
                .catch((error) => {
                    reject(error);
                });
        }), ServiceHelper.getTagForOptions(this.tags.getLastModifiedAppointmentDate, options));
    }

    // #endregion

    // #region Create/Update Appointment

    public static createAppointment = (hospitalId: number, appointment: IAppointmentRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments`)
                    .post<IAppointmentListingModel>(body).then((apt) => {
                        const appointment = new AppointmentListingModel(apt);
                        ServiceHelper.showSuccess("Appointment Created!", options);
                        resolve(appointment);
                    }).catch((error) => {
                        ServiceHelper.showError(`Failed to create appointment. ${error.message}`, options);
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.createAppointment, options));
    }

    public static updateAppointment = (hospitalId: number, reservationId: number, appointment: IAppointmentRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${reservationId}`)
                    .put<IAppointmentListingModel>(body).then((apt) => {
                        const appointment = new AppointmentListingModel(apt);
                        ServiceHelper.showSuccess("Appointment Updated!", options);
                        resolve(appointment);
                    }).catch((error) => {
                        ServiceHelper.showError(`Failed to update appointment. ${error.message}`, options);
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.updateAppointment, options));
    }

    public static checkInAppointment = (hospitalId: number, appointment: IAppointmentCheckInRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.reservationId}/checkin`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.checkInAppointment, options));
    }

    public static setNoShowAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.reservationId}/noshow`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setNoShowAppointment, options));
    }

    public static setInProgressAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.visitId}/movetoinprogress`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setInProgressAppointment, options));
    }

    public static setAdmittedAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.visitId}/movetoadmitted`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setAdmittedAppointment, options));
    }

    public static setBookedAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.reservationId}/movetobooked`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setBookedAppointment, options));
    }

    public static setConfirmedAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.reservationId}/movetoconfirmed`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setConfirmedAppointment, options));
    }

    public static setCancelledAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.reservationId}/movetocancelled`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setCancelledAppointment, options));
    }

    public static setFinalizedAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.visitId}/movetofinalized`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setFinalizedAppointment, options));
    }

    public static setCompletedAppointment = (hospitalId: number, appointment: IAppointmentStatusRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/appointments/${appointment.visitId}/movetocomplete`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setFinalizedAppointment, options));
    }

    public static setAbortedAppointment = (hospitalId: number, request: IVisitCheckoutRequest, options?: ServiceMethodOptions): Promise<AppointmentListingModel> => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}${hospitalId}/appointments/${request.visitId}/checkout`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), ServiceHelper.getTagForOptions(this.tags.setAbortedAppointment, options));
    }
    // #endregion

    //#region Get Hospital/s

    public static getHospital = (hospitalId: number, options?: ServiceMethodOptions): Promise<HospitalModel> => {
        return trackPromise(
            new Promise<HospitalModel>((resolve, reject) => {
                http(`${this.baseUrl}${hospitalId}`)
                    .get<IHospitalModel>().then((data) => {
                        try {
                            const hospital = new HospitalModel(data)
                            resolve(hospital);
                        }
                        catch (err) {
                            reject(err)
                        }
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.getHospital, options));
    }

    public static getHospitalForUser = (hospitalId: number, options?: ServiceMethodOptions): Promise<HospitalModel> => {
        return trackPromise(
            new Promise<HospitalModel>((resolve, reject) => {
                http(`${this.baseUrl}${hospitalId}/user`)
                    .get<IHospitalModel>().then((data) => {
                        try {
                            const hospital = new HospitalModel(data)
                            resolve(hospital);
                        }
                        catch (err) {
                            reject(err)
                        }
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.getHospital, options));
    }

    public static getAllHospitals = (options?: ServiceMethodOptions): Promise<HospitalModel[]> => {
        return trackPromise(
            new Promise<HospitalModel[]>((resolve, reject) => {
                http(`${this.baseUrl}getall`)
                    .get<IHospitalModel[]>().then((data) => {
                        try {
                            const hospitals = data.map(h => new HospitalModel(h));
                            resolve(hospitals);
                        }
                        catch (err) {
                            reject(err)
                        }
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.getAllHospitals, options));
    }

    public static getHospitalVeterinarians = (hospitalId: number, options?: ServiceMethodOptions): Promise<VeterinarianModel[]> => {
        return trackPromise(
            new Promise<VeterinarianModel[]>((resolve, reject) => {
                http(`${this.baseUrl}${hospitalId}/veterinarians`).get<IVeterinarianModel[]>().then((data) => {
                    try {
                        const vets = data.map(d => new VeterinarianModel(d));
                        ServiceHelper.showSuccess("Hospital Loaded!", options);
                        resolve(vets);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    ServiceHelper.showError(`Unable to get vets for hospital: ${hospitalId} - ${error.message}`, options);
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.getHospitalVeterinarians, options));
    }

    /**
     * New an improved Get Schedule end point, returned a ProcessedHospitalScheduleModel include the entire payload for a schedule.
     * @param hospitalId
     * @param date
     * @param range
     * @param options
     * @returns
     */
    public static getHospitalSchedules = (hospitalId: number, date: DateTime, range: HospitalScheduleDateRangeType, options?: ServiceMethodOptions): Promise<schedule.ProcessedHospitalScheduleModel> => {
        const endPoint = `${this.baseUrl}${hospitalId}/schedule/${date.toISODate()}/${range.toString()}`;

        return trackPromise(
            new Promise<schedule.ProcessedHospitalScheduleModel>((resolve, reject) => {
                http(endPoint).get<schedule.ProcessedHospitalScheduleModel>().then((data) => {
                    try {
                        const scheduleItem = new schedule.ProcessedHospitalScheduleModel(data);

                        ServiceHelper.showSuccess("Hospital Loaded!", options);
                        resolve(scheduleItem);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    ServiceHelper.showError(`Unable to get schedule for hospital: ${hospitalId} - ${error.message}`, options);
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.getHospitalSchedules, options));
    }

    public static getHospitalScheduleTemplate = (hospitalScheduleTemplateId: number, options?: ServiceMethodOptions): Promise<HospitalScheduleTemplateModel> => {
        const endPoint = `${this.baseUrl}schedule/template/${hospitalScheduleTemplateId}`;
        return trackPromise(
            new Promise<HospitalScheduleTemplateModel>((resolve, reject) => {
                http(endPoint).get<IHospitalScheduleTemplateModel>().then((data) => {
                    try {
                        const template = new HospitalScheduleTemplateModel(data);
                        ServiceHelper.showSuccess("Hospital Loaded!", options);
                        resolve(template);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    ServiceHelper.showError(`Unable to get hospital schdule template: ${hospitalScheduleTemplateId} - ${error.message}`, options);
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.getHospitalScheduleTemplate, options));
    }


    public static getHospitalScheduleTemplateList = (hospitalId: number, isActive?: boolean, options?: ServiceMethodOptions): Promise<HospitalScheduleTemplateModel[]> => {
        let endPoint = `${this.baseUrl}${hospitalId}/schedule/templates?isActive=${isActive != null ? isActive : null}`;

        return trackPromise(
            new Promise<HospitalScheduleTemplateModel[]>((resolve, reject) => {
                http(endPoint).get<IHospitalScheduleTemplateModel[]>().then((data) => {
                    try {
                        const ret = data.map(d => new HospitalScheduleTemplateModel(d))
                        resolve(ret);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.getHospitalScheduleTemplateList, options));
    }

    public static updateHospitalScheduleTemplate = (request: HospitalScheduleTemplateRequest, options?: ServiceMethodOptions) => {
        const endPoint = `${this.baseUrl}schedule/template/${request.hospitalScheduleTemplateId}`;
        return trackPromise(
            new Promise<HospitalScheduleTemplateModel>((resolve, reject) => {
                http(endPoint).put<IHospitalScheduleTemplateModel>(request).then((data) => {
                    try {
                        const template = new HospitalScheduleTemplateModel(data);
                        resolve(template);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.getHospitalScheduleTemplate, options));
    }
    // #endregion

    //#region Update Hospital/s
    public static updateHospitalSchedules = (hospitalId: number, date: DateTime, schedule: HospitalScheduleRequestModel[], options?: ServiceMethodOptions): Promise<boolean> => {
        return trackPromise(new Promise<boolean>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/schedule/${date.year}/${date.month}`)
                .put<boolean>(schedule)
                .then(data => {
                    ServiceHelper.showSuccess("Schedule Updated!", options);
                    resolve(data);
                })
                .catch((err) => {
                    ServiceHelper.showError(`Unable to update hospital schedules for hospital: ${hospitalId} - ${err ? err.message : ''}`)
                    reject(err);
                });
        }), ServiceHelper.getTagForOptions(this.tags.updateHospitalSchedules, options));
    }

    public static addAdhocHospitalSchedule = (hospitalId: number, schedule: HospitalScheduleRequestModel, options?: ServiceMethodOptions): Promise<HospitalScheduleModel> => {
        return trackPromise(new Promise<HospitalScheduleModel>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/schedule/add/adhoc`)
                .post<IHospitalScheduleModel>(schedule)
                .then(data => {
                    const schedule = new HospitalScheduleModel(data);
                    ServiceHelper.showSuccess("Schedule Created!", options);
                    resolve(schedule);
                })
                .catch((err) => {
                    ServiceHelper.showError(`Failed to create schedule for hospital: ${hospitalId} - ${err ? err.message : ''}`)
                    reject(err);
                });
        }), ServiceHelper.getTagForOptions(this.tags.addAdhocHospitalSchedule, options));
    }

    public static updateHospital = (hospitalId: number, hospital: HospitalModel, options?: ServiceMethodOptions): Promise<HospitalModel> => {
        return trackPromise(
            new Promise<HospitalModel>((resolve, reject) => {
                http(`/api/hospital/${hospitalId}`).put<IHospitalModel>(hospital).then((data) => {
                    try {
                        const updatedHospital = new HospitalModel(data);
                        resolve(updatedHospital);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.updateHospital, options));
    }

    public static convertStoreToHospital = (storeId: number, options?: ServiceMethodOptions): Promise<HospitalModel> => {
        return trackPromise(
            new Promise<HospitalModel>((resolve, reject) => {
                http(`/api/hospital/${storeId}`).post<IHospitalModel>({}).then((data) => {
                    try {
                        const hospital = new HospitalModel(data);
                        resolve(hospital);
                    }
                    catch (err) {
                        reject(err)
                    }
                }).catch((error) => {
                    reject(error);
                });
            }), ServiceHelper.getTagForOptions(this.tags.convertStoreToHospital, options));
    }

    public static createHospitalSchedulePreferenceOverrides = (hospitalId: number, date: string, request: EntityScheduleOverridePreferenceRequest[], options?: ServiceMethodOptions) => {
        return trackPromise(
            new Promise<null>((resolve, reject) => {
                http(`${this.baseUrl}${hospitalId}/preferenceOverrides?date=${date}`)
                    .post<null[]>(request)
                    .then(() => {
                        try {
                            resolve(null);
                        }
                        catch (err) {
                            reject(err)
                        }
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.createHospitalSchedulePreferenceOverrides, options));
    }

    public static updateHospitalSchedulePreferenceOverrides = (hospitalId: number, date: DateTime, request: EntityScheduleOverridePreferenceRequest[], options?: ServiceMethodOptions) => {
        return trackPromise(
            new Promise<null>((resolve, reject) => {
                http(`${this.baseUrl}${hospitalId}/preferenceOverrides?date=${date.toISO()}`)
                    .put<null>(request)
                    .then(() => {
                        try {
                            resolve(null);
                        }
                        catch (err) {
                            reject(err)
                        }
                    })
                    .catch((err) => {
                        reject(err);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.updateHospitalSchedulePreferenceOverrides, options));
    }

    public static reopenVisit = (hospitalId: number, appointment: IAppointmentStatusRequest) => {
        return trackPromise(
            new Promise<AppointmentListingModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(appointment));
                http(`${this.baseUrl}${hospitalId}/reopenvisit/${appointment.visitId}`)
                    .post<IAppointmentListingModel>(body)
                    .then(apt => new AppointmentListingModel(apt))
                    .then(resolve)
                    .catch(reject);
            }), this.tags.reopenVisit);
    }

    //#endregion

    // #region Hospital Prescription Refills

    public static createHospitalPrescriptionRefill = (hospitalId: number, request: IAddHospitalPrescriptionRefillRequest, options?: ServiceMethodOptions): Promise<HospitalPrescriptionRefillModel> => {
        return trackPromise(
            new Promise<HospitalPrescriptionRefillModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}${hospitalId}/hospitalprescriptionrefill`)
                    .post<IHospitalPrescriptionRefillModel>(body).then((apt) => {
                        const appointment = new HospitalPrescriptionRefillModel(apt);
                        resolve(appointment);
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.createHospitalPrescriptionRefill, options));
    }

    public static updateHospitalPrescriptionRefill = (hospitalId: number, request: IUpdateHospitalPrescriptionRefillRequest, options?: ServiceMethodOptions): Promise<HospitalPrescriptionRefillModel> => {
        return trackPromise(
            new Promise<HospitalPrescriptionRefillModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}${hospitalId}/hospitalprescriptionrefill/${request.hospitalPrescriptionRefillId}`)
                    .post<IHospitalPrescriptionRefillModel>(body).then((apt) => {
                        const appointment = new HospitalPrescriptionRefillModel(apt);
                        resolve(appointment);
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.updateHospitalPrescriptionRefill, options));
    }

    public static checkOutHospitalPrescriptionRefill = (hospitalId: number, request: ICheckOutHospitalPrescriptionRefillRequest, options?: ServiceMethodOptions): Promise<InvoiceModel> => {
        return trackPromise(
            new Promise<InvoiceModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}${hospitalId}/hospitalprescriptionrefill/${request.hospitalPrescriptionRefillId}/checkout`)
                    .post<IInvoiceModel>(body).then((apt) => {
                        const invoice = new InvoiceModel(apt);
                        resolve(invoice);
                    }).catch((error) => {
                        reject(error);
                    });
            }), ServiceHelper.getTagForOptions(this.tags.checkOutHospitalPrescriptionRefill, options));
    }

    //#endregion

    //#region Macros

    static getMacro = (macroId: number, macroTargetTypeId?: number, options?: ServiceMethodOptions): Promise<MacroModel> => {
        return trackPromise(new Promise<MacroModel>((resolve, reject) => {
            http(`${this.baseUrl}macros/${macroId}/${macroTargetTypeId}`).get<IMacroModel>()
                .then(json => new MacroModel(json))
                .then(resolve)
                .catch((err) => {
                    ServiceHelper.showError("Failed to fetch macro.");
                    reject(err);
                })
        }), ServiceHelper.getTagForOptions(this.tags.getMacro, options));
    };

    static createMacro = (macro: IMacroModel, options?: ServiceMethodOptions): Promise<MacroModel> => {
        const request = MacroRequest.ParseMacroRequest(macro);

        return trackPromise(new Promise<MacroModel>((resolve, reject) => {
            http(`${this.baseUrl}macros`)
                .post<IMacroModel>(request)
                .then(json => new MacroModel(json))
                .then(resolve)
                .catch(reject)
        }), ServiceHelper.getTagForOptions(this.tags.createMacro, options));
    }

    static updateMacro = (macro: IMacroModel, options?: ServiceMethodOptions): Promise<MacroModel> => {
        const request = MacroRequest.ParseMacroRequest(macro);

        return trackPromise(new Promise<MacroModel>((resolve, reject) => {
            http(`${this.baseUrl}macros/${macro.macroId}`)
                .post<IMacroModel>(request)
                .then(json => new MacroModel(json))
                .then(resolve)
                .catch(reject)
        }), ServiceHelper.getTagForOptions(this.tags.createMacro, options));
    }

    static deleteMacro = (macroId: number): Promise<void> => {
        return trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}macros/${macroId}`)
                .delete()
                .then(() => resolve())
                .catch(reject)
        }), this.tags.deleteMacro);
    }

    //#endregion

    //#region Hospital Prescription

    static upsertHospitalPrescription = (request: HospitalPrescriptionModel): Promise<HospitalPrescriptionModel> =>
        trackPromise(new Promise<HospitalPrescriptionModel>((resolve, reject) => {
            http(`${this.baseUrl}hospitalprescription`)
                .post<IHospitalPrescriptionModel>(request)
                .then(json => resolve(new HospitalPrescriptionModel(json)))
                .catch(reject)
        }), this.tags.upsertHospitalPrescription);

    static upsertHospitalPrescriptions = (hospitalId: number, request: HospitalPrescriptionModel[]): Promise<HospitalPrescriptionModel> =>
        trackPromise(new Promise<HospitalPrescriptionModel>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/hospitalprescriptions`)
                .post<IHospitalPrescriptionModel>(request)
                .then(json => resolve(new HospitalPrescriptionModel(json)))
                .catch(reject)
        }), this.tags.upsertHospitalPrescriptions);

    static deleteHospitalPrescription = (hospitalPrescriptionId: number): Promise<void> => {
        return trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}hospitalprescriptions/${hospitalPrescriptionId}`)
                .delete()
                .then(() => resolve())
                .catch(reject)
        }), this.tags.deleteHospitalPrescription);
    }

    static cancelHospitalPrescription = (hospitalPrescriptionId: number, cancelNotes: HospitalPrescriptionCancelModel): Promise<void> => {
        return trackPromise(new Promise<void>((resolve, reject) => {
            http(`${this.baseUrl}/hospitalprescriptions/${hospitalPrescriptionId}/cancel`)
                .post(cancelNotes)
                .then(() => resolve())
                .catch(reject)
        }), this.tags.cancelHospitalPrescription)
    }

    //#endregion

    static refreshHospitalIvlsDevices = (hospitalId: number, options?: ServiceMethodOptions): Promise<IIvlsDeviceModel[]> => {
        return trackPromise(new Promise<IIvlsDeviceModel[]>((resolve, reject) => {
            http(`${this.baseUrl}${hospitalId}/refreshivlsdevices`).get<IIvlsDeviceModel[]>()
                .then((response) => {
                    const ivlsDevices = response.map((iv) => new IvlsDeviceModel(iv));
                    ServiceHelper.showSuccess("Ivls Devices refreshed.", options);
                    resolve(ivlsDevices);
                })
                .catch((err) => {
                    ServiceHelper.showError("Failed to refresh Ivls Devices.", options);
                    reject(err);
                })
        }), this.tags.refreshHospitalIvlsDevices)
    }

    static createPetNotPresentVisit = (request: PetNotPresentVisitRequest): Promise<number> => {
        return trackPromise(
            new Promise<number>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}createPetNotPresentVisit`)
                    .post<number>(body)
                    .then((visitId) => {
                        resolve(visitId);
                    }).catch((error) => {
                        reject(error);
                    });
            }), this.tags.createPetNotPresentVisit);
    }

    static checkOutPetNotPresentVisit = (request: CheckoutPetNotPresentVisitRequest): Promise<InvoiceModel> => {
        return trackPromise(
            new Promise<InvoiceModel>((resolve, reject) => {
                const body = JSON.parse(JSON.stringify(request));
                http(`${this.baseUrl}checkOutPetNotPresentVisit/checkout`)
                    .post<IInvoiceModel>(body)
                    .then((apt) => {
                        const invoice = new InvoiceModel(apt);
                        resolve(invoice);
                    }).catch((error) => {
                        reject(error);
                    });
            }), this.tags.checkOutPetNotPresentVisit);
    }

    static cancelPetNotPresentVisit = (visitId: number): Promise<void> => {
        return trackPromise(
            new Promise<void>((resolve, reject) => {
                http(`${this.baseUrl}cancelPetNotPresentVisit/${visitId}`)
                    .post<void>(visitId)
                    .then(() => {
                        resolve();
                    }).catch((error) => {
                        reject(error);
                    });
            }), this.tags.cancelPetNotPresentVisit);
    }

    static cancelPetNotPresentInvoice = (visitId: number): Promise<void> => {
        return trackPromise(
            new Promise<void>((resolve, reject) => {
                http(`${this.baseUrl}cancelPetNotPresentInvoice/${visitId}`)
                    .post<void>(visitId)
                    .then(() => {
                        resolve();
                    }).catch((error) => {
                        reject(error);
                    });
            }), this.tags.cancelPetNotPresentInvoice);
    }
}
